import { TranslatePipe } from "@ngx-translate/core";

import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatTooltipModule } from "@angular/material/tooltip";

import { ReadableSizePipe } from "../../../helpers/bitspipe/bitspipe";
import { LightPathSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { AuthService } from "../../../services/authservice";
import { MessageService } from "../../../services/messageservice";
import { OverflowMenuButton } from "../../common/overflow-menu-button/overflow-menu-button";
import { serviceHealth } from "../../dashboard/service-health-signal/service-health-signal";
import { DialogAuthComponent } from "../../dialogs/dialog-auth/dialog-auth";
import { openSelfServiceDialog } from "../../dialogs/dialog-selfservice/dialog-selfservice.helpers";
import { SapTile } from "../sap-tile/sap-tile";

@Component({
  selector: "connection",
  templateUrl: "connection.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslatePipe, MatTooltipModule, SapTile, ReadableSizePipe, OverflowMenuButton],
})
export class Connection {
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);
  private messageService = inject(MessageService);

  protected circuit = input.required<LightPathSubscriptionDetails["circuits"][0]>();
  protected subscription = input.required<LightPathSubscriptionDetails>();
  protected connectionNumber = input(1);

  protected endpoints = computed(() => this.circuit().endpoints);

  protected fromSap = computed(() => this.endpoints()[0]);
  protected toSap = computed(() => this.endpoints()[1]);

  private fromSapPort = computed(() => this.fromSap().port);
  private toSapPort = computed(() => this.toSap().port);

  private fromHealth = serviceHealth(this.fromSapPort);
  private toHealth = serviceHealth(this.toSapPort);

  private productType = computed(() => this.subscription().product.type);

  protected fromHealthStatus = computed(() => this.fromHealth().status);
  protected toHealthStatus = computed(() => this.toHealth().status);

  protected trafficIn = computed(() => this.fromHealth().in);
  protected trafficOut = computed(() => this.fromHealth().out);

  protected fromCity = computed(() => this.fromSapPort()?.address?.city ?? "");
  protected toCity = computed(() => this.toSapPort()?.address?.city ?? "");

  private somePortsAreUntagged = computed(() =>
    this.endpoints().some((endpoint) => endpoint?.port?.portMode === "Untagged"),
  );
  protected isSelfServiceAvailable = computed(
    () =>
      this.auth.hasRole(["Infraverantwoordelijke"])
      && this.auth?.isCurrentOrganisation(this.subscription().customerId)
      && !this.somePortsAreUntagged(),
  );

  protected selfServiceEnabled = computed(() => !this.subscription().dirty);

  protected openSelfserviceDialog(type: string) {
    const roleCheckResult = this.auth.checkRoleAccess(this.productType(), "edit");
    if (!roleCheckResult.ok) {
      this.auth.roleEvent.emit(roleCheckResult);
      return;
    }

    if (!this.auth.selfServiceStarted()) {
      this.dialog.open(DialogAuthComponent, {
        data: {
          selfserviceState: { connectionNumber: this.connectionNumber() },
          initialAction: type,
        },
      });
    } else {
      const data = {
        type,
        circuit: this.circuit(),
        subscription: this.subscription(),
        titleSuffix: this.connectionNumber(),
      };

      const dialogRef = openSelfServiceDialog(this.dialog, data);
      dialogRef.afterClosed().subscribe((event: string) => {
        this.messageService.setSelfServiceMessage(event);
      });
    }
  }
}
